import axios from 'axios';
import { showToast } from '@/utils/toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `/api`;

/**
 * Centralized axios instance for all API calls
 * Handles authentication, token refresh, and error transformation
 */
const client = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

/**
 * Request interceptor - Adds authentication token to all requests
 */
client.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor - Handles token refresh and error transformation
 */
client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle 401 - Token expired, attempt refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }
        
        const response = await axios.post(`${API_BASE}/auth/refresh`, {
          refresh_token: refreshToken
        });
        
        const { access_token } = response.data;
        localStorage.setItem('access_token', access_token);
        
        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return client(originalRequest);
      } catch (refreshError) {
        // Refresh failed - logout user
        console.error('Token refresh failed:', refreshError);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        sessionStorage.removeItem('current_user');
        
        // Redirect to home page
        window.location.href = '/';
        return Promise.reject(refreshError);
      }
    }
    
    // Transform error to consistent structure
    const apiError = {
      error_type: error.response?.data?.error_type || 'unknown_error',
      message: error.response?.data?.detail || error.message || 'An error occurred',
      details: error.response?.data?.details || null,
      status_code: error.response?.status || 500
    };
    
    return Promise.reject(apiError);
  }
);

export default client;
